import { useEffect, useState } from "react";
import { fetchCaliberPhotos, fileUrl } from "@/lib/api";
import { ChevronDown, History } from "lucide-react";
import ServicePhotoViewer from "@/components/service/ServicePhotoViewer";

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export default function PastCaliberPhotos({ watchId, serviceId }) {
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null); // {g, i}

  useEffect(() => {
    setRes(null);
    if (!watchId) return;
    fetchCaliberPhotos(watchId, serviceId).then(setRes).catch(() => setRes(null));
  }, [watchId, serviceId]);

  if (!res?.groups?.length) return null;
  const total = res.groups.reduce((n, g) => n + g.photos.length, 0);

  return (
    <div className="border border-[#8B6A1F]/35 rounded-sm" data-testid="past-caliber-photos">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-[#2B2214]/[0.05] transition-colors"
        data-testid="past-caliber-photos-toggle"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] min-w-0">
          <History size={12} className="shrink-0" />
          <span className="break-words">Past photos of {res.caliber} <span className="font-mono-data">({total})</span></span>
        </span>
        <ChevronDown size={14} className={`shrink-0 text-[#3A2A10] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3" data-testid="past-caliber-photos-list">
          {res.groups.map((g) => (
            <div key={g.service_id} data-testid={`past-caliber-group-${g.service_id}`}>
              <div className="text-[11px] text-[#0A0604] mb-1.5 break-words">
                <span className="font-mono-data text-[#3A2A10]">#{g.watch_excel_id ?? "—"}</span>{" "}
                {[g.watch_brand, g.watch_model].filter(Boolean).join(" ") || "Unbranded"}
                <span className="text-[#3A2A10]"> · {g.service_type || "Service"}{g.date ? ` · ${fmt(g.date)}` : ""}</span>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                {g.photos.map((p, i) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setView({ g, i })}
                    className="relative aspect-square overflow-hidden rounded-sm border border-[#8B6A1F]/35 hover:border-[#D4AF37] transition-colors"
                    data-testid={`past-caliber-photo-${g.service_id}-${i + 1}`}
                  >
                    <img src={fileUrl(p.path, 160)} alt={`Photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                    <span className="absolute top-0.5 left-0.5 px-1 text-[9px] font-mono-data bg-[#0A0604]/75 text-[#F9F0D4] rounded-sm">{i + 1}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <ServicePhotoViewer
        photos={view?.g.photos || []}
        index={view ? view.i : null}
        onIndex={(i) => setView(i === null ? null : { g: view.g, i })}
        readOnly
      />
    </div>
  );
}
